import React, { createContext, useMemo, useRef, useContext, useEffect } from 'react';
import { nanoid } from 'nanoid/non-secure';
import { Tag, encodeSpecialCharacters, generateHash, flattenArray } from '@okapi/dom-utils';

const REACT_TAG_MAP = {
    accesskey: 'accessKey',
    charset: 'charSet',
    class: 'className',
    contenteditable: 'contentEditable',
    contextmenu: 'contextMenu',
    'http-equiv': 'httpEquiv',
    itemprop: 'itemProp',
    tabindex: 'tabIndex',
};
const HTML_TAG_MAP = Object.keys(REACT_TAG_MAP).reduce((obj, key) => {
    obj[REACT_TAG_MAP[key]] = key;
    return obj;
}, {});
const TAG_TYPES = ['meta', 'link', 'noscript', 'style', 'script', 'base'];

function tagsToString(type, tags) {
    return tags.map(t => t.toString()).join('');
}
const elementAttrToString = (attributes) => Object.keys(attributes).reduce((str, key) => {
    const attr = typeof attributes[key] !== 'undefined'
        ? `${key}="${encodeSpecialCharacters(attributes[key].toString())}"`
        : `${key}`;
    return str ? `${str} ${attr}` : attr;
}, '');
const convertElementAttributesToReactProps = (attributes, initProps = {}) => Object.keys(attributes).reduce((obj, key) => {
    obj[REACT_TAG_MAP[key] || key] = attributes[key];
    return obj;
}, initProps);
/**
 * Фабрика методов для TypedChildren
 * @param tagName
 * @param instances
 */
function getMethodsForTag(tagName, instances) {
    switch (tagName) {
        case 'htmlAttributes':
        case 'bodyAttributes':
            {
                const attrs = [...instances.values()].reduce((memo, curr) => {
                    if (typeof curr[tagName] !== 'undefined') {
                        return {
                            ...memo,
                            ...curr[tagName],
                        };
                    }
                    return memo;
                }, {});
                return {
                    toJSON() {
                        return attrs;
                    },
                    toString() {
                        return elementAttrToString(attrs);
                    },
                    toComponent() {
                        return convertElementAttributesToReactProps(attrs);
                    },
                };
            }
        case 'title':
            {
                const element = [...instances.values()].reduce((memo, curr) => {
                    if (typeof curr.title !== 'undefined') {
                        memo.children = memo.children ? `${memo.children} ${curr.title}` : curr.title;
                    }
                    if (typeof curr.titleAttributes !== 'undefined') {
                        memo.mergeProps(curr.titleAttributes);
                    }
                    return memo;
                }, new Tag('title', {}));
                return {
                    toJSON() {
                        return element;
                    },
                    toString() {
                        return tagsToString('title', [element]);
                    },
                    toComponent() {
                        return React.createElement(element.type, element.props, element.children);
                    },
                };
            }
        default:
            {
                const tags = [...instances.values()].reduce((memo, curr) => {
                    const t = curr[tagName];
                    if (typeof t !== 'undefined') {
                        return memo.concat(t);
                    }
                    return memo;
                }, []);
                return {
                    toJSON() {
                        return tags;
                    },
                    toString() {
                        return tagsToString(tagName, tags);
                    },
                    toComponent() {
                        return tags.map(l => React.createElement(l.type, l.props, l.children));
                    },
                };
            }
    }
}

/**
 * Запускает обновление HTML тэгов в DOM
 */
const updateTags = (key, type, tags = []) => {
    const headElement = document.head || document.querySelector('head');
    const currentTags = Array.from(headElement.querySelectorAll(`${type}[data-helmet="${key}"]`));
    const newTags = [];
    if (tags && tags.length) {
        tags.forEach(tag => {
            const newElement = tag.toHTMLElement();
            newElement.dataset.helmet = key;
            let indexToDelete = null;
            let i = 0;
            while (i < currentTags.length && indexToDelete === null) {
                if (newElement.isEqualNode(currentTags[i])) {
                    indexToDelete = i;
                    currentTags.splice(indexToDelete, 1);
                }
                i += 1;
            }
            if (indexToDelete === null) {
                newTags.push(newElement);
            }
        });
    }
    currentTags.forEach((tag) => tag.parentNode && tag.parentNode.removeChild(tag));
    newTags.forEach((tag) => headElement.appendChild(tag));
};
/**
 * Запускает обновление аттрибутов элемента
 */
const updateAttributes = (id, tagName, attributes = {}) => {
    const elementTag = document.getElementsByTagName(tagName)[0];
    if (!elementTag) {
        return;
    }
    const helmetAttributeString = elementTag.getAttribute(`data-${id}`);
    const attributesToRemove = helmetAttributeString ? helmetAttributeString.split(',') : [];
    const attributesToAdd = Object.keys(attributes);
    for (let i = 0; i < attributesToAdd.length; i += 1) {
        const attribute = attributesToAdd[i];
        const value = attributes[attribute] || '';
        if (elementTag.getAttribute(attribute) !== value) {
            elementTag.setAttribute(attribute, value.toString());
        }
        const indexToSave = attributesToRemove.indexOf(attribute);
        if (indexToSave !== -1) {
            attributesToRemove.splice(indexToSave, 1);
        }
    }
    for (let i = attributesToRemove.length - 1; i >= 0; i -= 1) {
        elementTag.removeAttribute(attributesToRemove[i]);
    }
    if (attributesToAdd.length) {
        elementTag.setAttribute(`data-${id}`, attributesToAdd.join(','));
    }
    else {
        elementTag.removeAttribute(`data-${id}`);
    }
};
/**
 * Обновление элемента title
 * @param id
 * @param title
 * @param attributes
 */
const updateTitle = (id, title, attributes = {}) => {
    if (typeof title !== 'undefined' && document.title !== title) {
        document.title = title;
    }
    updateAttributes(id, 'title', attributes);
};
/**
 * Применяет обновление элементов
 */
const commitTagChanges = (id, newState, cb) => {
    updateAttributes(id, 'body', newState.bodyAttributes);
    updateAttributes(id, 'html', newState.htmlAttributes);
    updateTitle(id, newState.title, newState.titleAttributes);
    if (newState.base) {
        updateTags(id, 'base', [newState.base]);
    }
    updateTags(id, 'link', newState.link);
    updateTags(id, 'meta', newState.meta);
    updateTags(id, 'noscript', newState.noscript);
    updateTags(id, 'script', newState.script);
    updateTags(id, 'style', newState.style);
    if (cb) {
        cb();
    }
};
let _helmetCallback = null;
/**
 * Запускает обновление элементов
 * @param {string} id идентификатор элемента
 * @param {Partial<TypedChildren>} newState объект с обновлениями
 */
const handleStateChangeOnClient = (id, newState) => {
    if (_helmetCallback) {
        cancelAnimationFrame(_helmetCallback);
    }
    if (newState.defer) {
        _helmetCallback = requestAnimationFrame(() => {
            commitTagChanges(id, newState, () => {
                _helmetCallback = null;
            });
        });
    }
    else {
        commitTagChanges(id, newState);
        _helmetCallback = null;
    }
};
/**
 * Склеивает HTML элементы из DOM с элементами в {@link TagsStore}
 * @param {TagsStore} tagsStore хранилище с ключами
 */
function hydrateTagsOnClient(tagsStore) {
    const headElement = document.head || document.querySelector('head');
    TAG_TYPES.forEach(type => {
        const tags = Array.from(headElement.querySelectorAll(`${type}`));
        tags.forEach(tag => {
            let t = `${type}`;
            Array.from(tag.attributes)
                .map(t => String(t.name))
                .sort()
                .forEach(prop => {
                const p = tag.attributes.getNamedItem(prop);
                t += `[${prop.toLowerCase()}="${encodeSpecialCharacters(((p !== null && p.value) || ''))}"]`;
            });
            if (tag.innerHTML) {
                t += tag.innerHTML;
            }
            const hash = tagsStore.get(generateHash(t));
            if (hash) {
                tag.setAttribute('data-helmet', hash);
            }
        });
    });
}

/**
 * Обработчик
 */
class HelmetHandler {
    constructor() {
        this.instances = new Map();
        this.index = 0;
        this.stack = [];
        this.tagsStore = new Map();
        if (typeof window !== 'undefined' && window.tagsStore) {
            const tagsStore = new Map(window.tagsStore);
            hydrateTagsOnClient(tagsStore);
            this.index = tagsStore.size;
        }
    }
    /**
     * Применить обновления тэгов
     * @param key
     * @param tags
     */
    update(key, tags) {
        if (key) {
            this.instances.set(key, tags);
            if (typeof window !== 'undefined') {
                this.commit(key, tags);
            }
        }
    }
    /**
     * Получить новый ключ в стеке
     */
    getStackId() {
        if (!this.stack[this.index]) {
            this.stack[this.index] = nanoid(6);
        }
        const key = this.stack[this.index];
        this.index += 1;
        return key;
    }
    /**
     * Сбросить ключ в стеке
     */
    resetStackIndex() {
        this.index = 0;
    }
    /**
     * Удалить элемент из обраточика и DOM
     * @param key
     */
    delete(key) {
        if (key) {
            this.instances.delete(key);
            this.stack = this.stack.filter(s => s !== key);
            this.index -= 1;
            if (typeof window !== 'undefined') {
                this.commit(key, {});
            }
        }
    }
    /**
     * Запустить обновление HTML тегов в DOM
     * @param id
     * @param tags
     */
    commit(id, tags) {
        handleStateChangeOnClient(id, tags);
    }
    /**
     * Получить сериализованное значение всех имеющихся тегов
     */
    serialize() {
        const tagsStore = new Map(this.tagsStore.entries());
        Array.from(this.instances.entries()).forEach(([k, instance]) => {
            TAG_TYPES.forEach(type => {
                if (type === 'base' && instance.base) {
                    const hash = this.serializeTag(instance.base);
                    tagsStore.set(hash, k);
                }
                else {
                    const tags = instance[type];
                    if (tags) {
                        for (const tag of tags) {
                            const hash = this.serializeTag(tag);
                            tagsStore.set(hash, k);
                        }
                    }
                }
            });
        });
        return [...tagsStore.entries()];
    }
    /**
     * Получить хэш элемента
     */
    serializeTag(tag) {
        return tag.getHash();
    }
    get base() {
        return getMethodsForTag('base', this.instances);
    }
    get link() {
        return getMethodsForTag('link', this.instances);
    }
    get meta() {
        return getMethodsForTag('meta', this.instances);
    }
    get noscript() {
        return getMethodsForTag('noscript', this.instances);
    }
    get script() {
        return getMethodsForTag('script', this.instances);
    }
    get style() {
        return getMethodsForTag('style', this.instances);
    }
    get bodyAttributes() {
        return getMethodsForTag('bodyAttributes', this.instances);
    }
    get htmlAttributes() {
        return getMethodsForTag('htmlAttributes', this.instances);
    }
    get title() {
        return getMethodsForTag('title', this.instances);
    }
}

const Context = createContext({});

/**
 * Provider для компонента Helmet
 * на клиенте работает как синглтон
 * @param {HelmetProviderProps} props
 */
const Provider = ({ children, context = {} }) => {
    const localContext = useMemo(() => {
        if (!context.helmet) {
            if (typeof window !== 'undefined') {
                if (!window.helmet) {
                    window.helmet = new HelmetHandler();
                }
                context.helmet = window.helmet;
            }
            else {
                context.helmet = new HelmetHandler();
            }
        }
        return context;
    }, [context]);
    return React.createElement(Context.Provider, { value: localContext }, children);
};

function mapChildrenToProps(children, newProps) {
    React.Children.forEach(children, child => {
        if (!child || !child.props) {
            return;
        }
        const type = child.type.toString();
        const { children, ...childProps } = child.props;
        const props = Object.keys(childProps).reduce((obj, key) => {
            obj[HTML_TAG_MAP[key] || key] = childProps[key];
            return obj;
        }, {});
        switch (type) {
            case 'Symbol(react.fragment)':
                newProps = mapChildrenToProps(children, newProps);
                break;
            case 'style':
            case 'script':
            case 'noscript':
            case 'link':
            case 'meta':
                {
                    newProps = addElementToTypeChildren({
                        type,
                        props,
                        children,
                    }, newProps);
                }
                break;
            default:
                newProps = mapObjectTypeChildren({
                    type,
                    props,
                    children,
                }, newProps);
                break;
        }
    });
    return newProps;
}
function addElementToTypeChildren({ type, props, children, }, typedChildren) {
    return {
        ...typedChildren,
        [type]: (typedChildren[type] || []).concat([
            new Tag(type, props, mapNestedChildrenToProps(type, children)),
        ]),
    };
}
function mapNestedChildrenToProps(type, nestedChildren) {
    if (!nestedChildren) {
        return [];
    }
    switch (type) {
        case 'script':
        case 'noscript':
        case 'style':
            return nestedChildren;
        default:
            return [];
    }
}
function mapObjectTypeChildren({ type, props, children, }, typedChildren) {
    switch (type) {
        case 'title':
            return {
                ...typedChildren,
                title: children ? flattenArray(String(children)) : typedChildren.title,
                titleAttributes: { ...typedChildren.titleAttributes, ...props },
            };
        case 'body':
            return {
                ...typedChildren,
                bodyAttributes: { ...typedChildren.bodyAttributes, ...props },
            };
        case 'html':
            return {
                ...typedChildren,
                htmlAttributes: { ...typedChildren.htmlAttributes, ...props },
            };
        case 'base': {
            const base = typedChildren.base || new Tag('base', {});
            base.mergeProps(props);
            return {
                ...typedChildren,
                base,
            };
        }
        default:
            return {
                ...typedChildren,
                [type]: { ...typedChildren[type], ...props },
            };
    }
}

function useId(data = {}) {
    const usedIdentifier = useRef();
    if (!usedIdentifier.current) {
        usedIdentifier.current = generateHash(`helmet:${JSON.stringify(data)}`);
    }
    return usedIdentifier.current;
}

/**
 * Базовый компонент
 * @param {HelmetProps} props
 * ```typescript
 * import { Helmet, Provider as HelmetProvider } from '@okapi/helmet-react';
 * ...
 * export function App({ helmetContext }) {
 *  return (
 *   <HelmetProvider context={helmetContext}>
 *    <Helmet>
 *     <meta content="property" name="value" />
 *    </Helmet>
 *   </HelmetProvider>
 *  );
 * }
 * ```
 *
 */
const Helmet = props => {
    const context = useContext(Context);
    const newProps = useMemo(() => {
        const { children, ...returnProps } = props;
        return mapChildrenToProps(children, returnProps);
    }, [props]);
    const id = useId(newProps);
    useEffect(() => {
        if (context.helmet) {
            context.helmet.update(id, newProps);
        }
        return () => {
            if (context.helmet) {
                context.helmet.delete(id);
            }
        };
    }, [context.helmet, id, newProps]);
    if (typeof window === 'undefined') {
        if (context.helmet) {
            context.helmet.update(id, newProps);
        }
    }
    return null;
};

export { Helmet, HelmetHandler, Provider };
